'use client'
import type { MapEvent } from '@vis.gl/react-google-maps'
import { Map, Marker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import Image from 'next/image'
import type { CSSProperties, FC } from 'react'
import { useRef } from 'react'
import { useMemo } from 'react'
import React, { useEffect, useState } from 'react'
import { defaultPosition, globalZoom, userCircleConfig, userZoom } from './constants'
import styles from './GenericMap.module.scss'
import { getImageUrl } from '@/tools/image-service/client'
import { useErrorHandling } from '@/tools/o11y/client'
import { addressLandingOptionClickedEvent } from '@/user/address-book/analytics'
import type { GoogleCoordinates } from '@/user/address-book/types'
import { pinIconPaths } from '@/user/address-book/ui/constants'

type Props = {
  mapId: string
  style?: CSSProperties
  isOutOfZone?: boolean
  tooltip?: React.ReactNode
  onLocationChange?: (location: GoogleCoordinates) => void
  zoomOverride?: number
  onDragStart?: () => void
  positionOverride?: GoogleCoordinates
  userMarkPosition?: GoogleCoordinates
  gestureHandling?: 'greedy' | 'cooperative' | 'auto' | 'none'
}

export const GenericMap: FC<Props> = ({
  style,
  isOutOfZone,
  mapId,
  onLocationChange,
  tooltip,
  onDragStart,
  zoomOverride,
  positionOverride,
  userMarkPosition,
  gestureHandling = 'greedy',
}) => {
  const { execWithErrorTracking, withErrorTracking } = useErrorHandling()

  const isUserPositionSet = useRef(false)
  const isCenteringAutomatically = useRef(false)
  const [isMoving, setIsMoving] = useState<boolean>(false)
  const map = useMap(mapId)
  const coreLib = useMapsLibrary('core')

  useEffect(() => {
    execWithErrorTracking(() => {
      if (!userMarkPosition || !map || positionOverride || isUserPositionSet.current) {
        return
      }
      // Autopan and autozoom the map to the user's position unless these values are overridden
      // by respective props. We do this only once.
      isCenteringAutomatically.current = true
      map?.panTo(userMarkPosition)
      if (!zoomOverride) {
        map?.setZoom(userZoom)
      }
      isUserPositionSet.current = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, userMarkPosition])

  useEffect(() => {
    execWithErrorTracking(() => {
      // Reposition map on each change of positionOverride
      if (!positionOverride) {
        return
      }
      map?.panTo(positionOverride)
    })
  }, [map, positionOverride, execWithErrorTracking])

  const pinIconPath = useMemo(
    () =>
      getImageUrl({
        source: isMoving
          ? pinIconPaths.pinMoving
          : isOutOfZone
            ? pinIconPaths.pinOutOfZone
            : pinIconPaths.pinIcon,
      }),
    [isMoving, isOutOfZone]
  )

  const handlePositionChanged = withErrorTracking((event: MapEvent) => {
    setIsMoving(false)
    const center = event.map.getCenter()
    if (center && onLocationChange && !isCenteringAutomatically.current) {
      onLocationChange({
        lat: center.lat(),
        lng: center.lng(),
      })
      addressLandingOptionClickedEvent('PinOnMap')
    }
    if (isCenteringAutomatically.current) {
      isCenteringAutomatically.current = false
    }
  })

  const handleOnIdle = withErrorTracking((event: MapEvent) => {
    if (isMoving) {
      // We act only if interaction was started by user because onIdle triggers
      // on almost every event happening to map (like resize, mount, etc.)
      handlePositionChanged(event)
    }
  })

  const handleDragstart = withErrorTracking(() => {
    setIsMoving(true)
    if (onDragStart) {
      onDragStart()
    }
  })

  return (
    <div className={styles.genericMap} style={style}>
      <Map
        defaultCenter={positionOverride || defaultPosition}
        defaultZoom={zoomOverride || globalZoom}
        fullscreenControl={false}
        gestureHandling={gestureHandling}
        id={mapId}
        mapTypeControl={false}
        streetViewControl={false}
        zoomControlOptions={{ position: coreLib?.ControlPosition?.BLOCK_START_INLINE_END }}
        onDragstart={handleDragstart}
        onIdle={handleOnIdle}
        onZoomChanged={handlePositionChanged}
      >
        {userMarkPosition && coreLib && (
          <Marker
            icon={{
              path: coreLib?.SymbolPath?.CIRCLE,
              ...userCircleConfig,
            }}
            position={userMarkPosition}
          />
        )}
      </Map>
      <div className={styles.overMap}>
        <Image alt="address pin" height={68} src={pinIconPath} width={86} />
        {tooltip}
      </div>
    </div>
  )
}

export type { Props as GenericMapProps }
